import { z } from 'zod';
import { assetKindSchema } from './enums.js';
/**
 * Contrats des FICHIERS ABSENTS (`assets.is_missing = true`) — audit de remédiation,
 * constat A5. Source unique de vérité front + back.
 *
 * CONSTAT : le scanner marque `is_missing = true` toute ligne dont le fichier a disparu
 * du disque (jamais de suppression automatique — cf. `AssetsRepo.markMissing`), et le job
 * `dedup_apply` en CRÉE volontairement (« jamais de hard-delete DB »). Or
 * `assets-query-repo.ts` ajoute `is_missing = false` à TOUTES les requêtes de liste : ces
 * lignes devenaient invisibles ET indestructibles — ni sélectionnables, ni mettables à la
 * corbeille, ni purgeables. Seule la réinitialisation complète de bibliothèque les
 * éliminait.
 *
 * DÉCISION PRODUIT : elles sont exposées dans la CONSOLE ADMIN de galerie (`/admin`,
 * onglet « Fichiers absents », à côté de « Erreurs de fichiers ») et NON par un onglet de
 * galerie de plus. Motifs :
 *  1. le fichier n'existe PLUS sur disque → une grille de vignettes serait cassée par
 *     construction (chaque miniature en 404) ; une TABLE de chemins est honnête ;
 *  2. c'est une tâche de MAINTENANCE, pas de navigation ;
 *  3. la console est DÉJÀ gardée en écriture (`requireLibraryWrite`), ce que l'exigence
 *     « réservé à l'écriture » demande, sans ajouter de gating dans la galerie.
 *
 * La suite du parcours RÉUTILISE l'existant sans une ligne de code neuve :
 * `POST /api/libraries/:id/assets/soft-delete` (corbeille) puis la purge définitive de
 * l'onglet « Poubelle » (qui gère déjà `ENOENT` — le fichier est absent par définition).
 *
 * ⚠ PIÈGE ZOD v4 (boot crash) : le schéma de QUERY passe par `z.toJSONSchema` au
 * démarrage → aucun `.transform` ici (uniquement `z.coerce.number` + bornes).
 */
/** Bornes de pagination du listing (protège la DB + le rendu). Calquées sur la corbeille. */
export const MISSING_ASSETS_MAX_LIMIT = 200;
export const MISSING_ASSETS_DEFAULT_LIMIT = 50;
/**
 * Query de `GET /api/libraries/:id/missing-assets`. `limit` borné (1..200, défaut 50),
 * `offset` ≥ 0. Coercition depuis la querystring (AJV coerce, Zod re-valide).
 */
export const missingAssetsQuerySchema = z.object({
    limit: z.coerce
        .number()
        .int()
        .positive()
        .max(MISSING_ASSETS_MAX_LIMIT)
        .default(MISSING_ASSETS_DEFAULT_LIMIT),
    offset: z.coerce.number().int().nonnegative().default(0),
});
/**
 * Ligne « fichier absent » : de quoi identifier le média disparu (chemin, nom, type,
 * taille et date connues au dernier scan) et décider de son sort. AUCUNE vignette n'est
 * proposée : l'original n'existe plus.
 *
 * `seenAt` = dernière fois que le scanner a VU le fichier sur disque (donc « disparu
 * depuis au plus tard … ») ; `null` si la colonne n'a jamais été renseignée.
 */
export const missingAssetSchema = z.object({
    id: z.uuid(),
    relPath: z.string(),
    filename: z.string(),
    kind: assetKindSchema,
    mime: z.string(),
    sizeBytes: z.number().int().nonnegative(),
    takenAt: z.string().datetime({ offset: true }).nullable(),
    seenAt: z.string().datetime({ offset: true }).nullable(),
});
/** Page de fichiers absents (`{ assets, total }`) — `total` dimensionne la pagination. */
export const missingAssetsListSchema = z.object({
    assets: z.array(missingAssetSchema),
    total: z.number().int().nonnegative(),
});
//# sourceMappingURL=missing-assets.js.map